import { useState } from 'react'
import { Checkbox } from './Checkbox'
import { ChevronDown } from 'lucide-react'

export interface CheckboxDropdownOption {
  value: string
  label: string
}

interface CheckboxDropdownProps {
  label: string
  options: CheckboxDropdownOption[]
  value: string[]
  onChange: (value: string[]) => void
  allLabel?: string
}

/** Filtro multi-selezione compatto, con scelta resa esplicita tramite checkbox. */
export function CheckboxDropdown({ label, options, value, onChange, allLabel = 'Tutti' }: CheckboxDropdownProps) {
  const [query, setQuery] = useState('')
  const searchable = options.length > 6
  const normalizedQuery = query.trim().toLocaleLowerCase('it')
  const filteredOptions = normalizedQuery
    ? options.filter(option => option.label.toLocaleLowerCase('it').includes(normalizedQuery))
    : options
  const summary = value.length === 0
    ? allLabel
    : value.length === 1
      ? options.find((option) => option.value === value[0])?.label ?? `${value.length} selezionato`
      : `${value.length} selezionati`

  const toggle = (optionValue: string) => {
    onChange(value.includes(optionValue)
      ? value.filter((item) => item !== optionValue)
      : [...value, optionValue])
  }

  return (
    <details className="relative min-w-0" onToggle={event => { if (!event.currentTarget.open) setQuery('') }}>
      <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-2 rounded-md border border-border-strong bg-canvas px-3 py-2 text-sm text-body shadow-sm marker:hidden [&::-webkit-details-marker]:hidden">
        <span className="truncate">{summary}</span>
        <ChevronDown size={15} className="shrink-0 text-muted" aria-hidden="true" />
      </summary>
      <div className="absolute left-0 top-full z-dropdown mt-1 flex max-h-72 min-w-full flex-col overflow-hidden rounded-md border border-border bg-canvas p-2 shadow-lg">
        {searchable && <input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => event.stopPropagation()}
          placeholder="Cerca collega…"
          aria-label={`Cerca in ${label}`}
          className="mb-2 min-h-9 shrink-0 rounded-md border border-border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        />}
        <div className="min-h-0 overflow-y-auto">
        {filteredOptions.map((option) => (
          <div key={option.value} className="px-2 py-1.5">
            <Checkbox
              id={`${label}-${option.value}`}
              checked={value.includes(option.value)}
              onChange={() => toggle(option.value)}
              label={option.label}
            />
          </div>
        ))}
        {filteredOptions.length === 0 && <p className="px-2 py-2 text-sm text-muted">Nessun collega trovato</p>}
        </div>
        {value.length > 0 && (
          <button type="button" className="mt-1 w-full border-t border-border px-2 pt-2 text-left text-xs font-medium text-primary-700" onClick={() => onChange([])}>
            Azzera selezione
          </button>
        )}
      </div>
    </details>
  )
}
